import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { dateTimeLabel, relativeTime } from '../lib/format';
import { ArchStatusTag, IssueStatusTag, LoadingPage, Spinner, Tag, ViolationStatusTag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

export function NeighborhoodPage() {
  const { user, isBoard, isHomeowner, community, canUseFinance } = useAuth();

  const feed = useAsync(
    async () => {
      const [activity, events, issues, violations, arch] = await Promise.all([
        api.activity(12),
        api.events('upcoming'),
        api.issues(),
        api.violations(),
        isHomeowner ? api.archList() : Promise.resolve({ submissions: [] }),
      ]);
      return {
        activity: activity.activity,
        events: events.events,
        submissions: arch.submissions,
        issues: issues.issues,
        violations: violations.violations,
      };
    },
    [user?.id, isHomeowner],
  );

  if (feed.loading && !feed.data) return <LoadingPage label="Loading your neighborhood…" />;
  if (!feed.data) return null;

  const { activity, events, submissions, issues, violations } = feed.data;

  const myOpenIssues = issues.filter(
    (issue) =>
      issue.authorId === user?.id && issue.status !== 'Resolved' && issue.status !== 'Closed',
  );
  const myPendingArch = submissions.filter(
    (item) =>
      item.authorId === user?.id &&
      (item.status === 'Submitted' || item.status === 'In Review' || item.status === 'Info Requested'),
  );
  const myOpenViolations = violations.filter(
    (item) =>
      item.authorId === user?.id && item.status !== 'Resolved' && item.status !== 'Dismissed',
  );
  const nextEvents = events.slice(0, 3);
  const firstName = user?.name.split(' ')[0] ?? 'neighbor';

  return (
    <div>
      <PageHeader
        title={`Hello, ${firstName}`}
        lede={`${community?.name ? `${community.name}. ` : ''}${
          myPendingArch.length + myOpenIssues.length + myOpenViolations.length === 0
            ? 'Nothing of yours is waiting on the board. Here is what is happening around the neighborhood.'
            : `You have ${myPendingArch.length + myOpenIssues.length + myOpenViolations.length} item${
                myPendingArch.length + myOpenIssues.length + myOpenViolations.length === 1 ? '' : 's'
              } in progress.`
        }`}
      />

      {community && (community.boardEmail || community.managementEmail || community.boardContactName) && (
        <div className="card">
          <h3>{community.name}</h3>
          {community.address && <p className="mut">{community.address}</p>}
          <p className="mut" style={{ margin: 0 }}>
            {[
              community.boardContactName ? `Board: ${community.boardContactName}` : null,
              community.boardEmail,
              community.managementEmail ? `Management: ${community.managementEmail}` : null,
              community.managementPhone,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      )}

      <div className="grid">
        <div className="card">
          <h3>Quick actions</h3>
          <p className="mut">Most things start here.</p>
          <div className="btn-row">
            {isHomeowner && (
              <Link className="btn" to="/architecture">
                Submit a project
              </Link>
            )}
            <Link className="btn s" to="/requests">
              Report an issue
            </Link>
            <Link className="btn s" to="/violations">
              Report a violation
            </Link>
            <Link className="btn s" to="/events">
              See events
            </Link>
            <Link className="btn s" to="/bylaws">
              Ask about bylaws
            </Link>
          </div>
        </div>

        <div className="card">
          <h3>Coming up</h3>
          {nextEvents.length === 0 ? (
            <p className="mut">Nothing on the calendar yet.</p>
          ) : (
            <div className="stack" style={{ gap: 8, marginTop: 10 }}>
              {nextEvents.map((event) => (
                <div key={event.id}>
                  <div style={{ fontWeight: 500 }}>{event.title}</div>
                  <div className="mut">
                    {dateTimeLabel(event.startsAt, event.allDay)}
                    {event.location ? ` · ${event.location}` : ''}
                    {event.rsvpEnabled ? ` · ${event.goingCount} going` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="btn-row">
            <Link className="btn s tiny" to="/events">
              Full calendar
            </Link>
          </div>
        </div>
      </div>

      {(myPendingArch.length > 0 || myOpenIssues.length > 0 || myOpenViolations.length > 0) && (
        <>
          <h2>Your open items</h2>
          <div className="card">
            {myPendingArch.map((item) => (
              <div key={item.id} className="row between" style={{ padding: '8px 0' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{item.title}</div>
                  <div className="mut">
                    {item.reference} · architectural review · updated {relativeTime(item.updatedAt)}
                  </div>
                </div>
                <ArchStatusTag status={item.status} />
              </div>
            ))}
            {myOpenIssues.map((item) => (
              <div key={item.id} className="row between" style={{ padding: '8px 0' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{item.title}</div>
                  <div className="mut">
                    {item.reference} · {item.kind.toLowerCase()} · filed {relativeTime(item.createdAt)}
                  </div>
                </div>
                <IssueStatusTag status={item.status} />
              </div>
            ))}
            {myOpenViolations.map((item) => (
              <div key={item.id} className="row between" style={{ padding: '8px 0' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{item.propertyAddress}</div>
                  <div className="mut">
                    {item.reference} · violation · filed {relativeTime(item.createdAt)}
                  </div>
                </div>
                <ViolationStatusTag status={item.status} />
              </div>
            ))}
          </div>
        </>
      )}

      <h2>Latest activity</h2>
      {feed.loading && <Spinner />}
      {activity.length === 0 ? (
        <div className="card">
          <p className="mut">Nothing has happened yet. Be the first — submit a project or file a request.</p>
        </div>
      ) : (
        <div className="card">
          {activity.map((entry) => (
            <div key={entry.id} className="row between" style={{ padding: '7px 0', gap: 12 }}>
              <span>{entry.summary}</span>
              <span className="mut nowrap">{relativeTime(entry.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {isBoard && (
        <div className="card">
          <div className="row between">
            <div>
              <h3>Board tools</h3>
              <p className="mut" style={{ margin: 0 }}>
                Triage, financials, newsletters and resident roles.
              </p>
            </div>
            <Tag tone="info">Board</Tag>
          </div>
          <div className="btn-row">
            <Link className="btn s" to="/board">
              Board hub
            </Link>
            {canUseFinance && (
              <Link className="btn s" to="/financials">
                Financials
              </Link>
            )}
            <Link className="btn s" to="/newsletter">
              Newsletter
            </Link>
            <Link className="btn s" to="/board/residents">
              Member Management
            </Link>
            <Link className="btn s" to="/community">
              Community Management
            </Link>
            <Link className="btn s" to="/violations">
              Violations
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
